import { FC, useCallback, useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DatePicker,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Checkbox,
} from '@features/ui';
import { useProductOptions } from '@hooks/useProductOptions';
import { addMonths } from 'date-fns';
import { ProductTypes } from '@src/types/productsAddons';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';

interface AddPackageDialogProps {
  open: boolean;
  userId: number | null;
  onOpenChange: (open: boolean) => void;
  onSave: (data: {
    product_id: number;
    leeloo_user_id: number;
    valid_from: string;
    valid_to: string;
    reason?: string;
    send_mail_new_account?: boolean;
  }) => Promise<void>;
}

export const AddPackageDialog: FC<AddPackageDialogProps> = ({
  open,
  userId,
  onOpenChange,
  onSave,
}) => {
  const { productOptions } = useProductOptions({
    categories: [ProductTypes.TRADING_ACCOUNT],
  });
  const [selectedProduct, setSelectedProduct] = useState<string>('');
  const [validFrom, setValidFrom] = useState<Date | undefined>();
  const [validTo, setValidTo] = useState<Date | undefined>();
  const [reason, setReason] = useState<string>('');
  const [reasonError, setReasonError] = useState<string>('');
  const [sendEmail, setSendEmail] = useState<boolean>(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      // Reset form when dialog opens
      setSelectedProduct('');
      const today = new Date();
      const threeMonthsLater = addMonths(today, 3);

      setValidFrom(today);
      setValidTo(threeMonthsLater);
      setReason('');
      setReasonError('');
      setSendEmail(false);
    }
  }, [open]);

  const handleSave = useCallback(async () => {
    if (!selectedProduct || !userId || !validFrom || !validTo) return;

    if (!reason.trim()) {
      setReasonError('Reason is required');

      return;
    }

    setReasonError('');
    setLoading(true);
    try {
      const validFromStr = dayjs(validFrom).format(
        'YYYY-MM-DDTHH:mm:ss.sss[Z]'
      );
      const validToStr = dayjs(validTo).format('YYYY-MM-DDTHH:mm:ss.sss[Z]');

      await onSave({
        product_id: Number(selectedProduct),
        leeloo_user_id: userId,
        valid_from: validFromStr,
        valid_to: validToStr,
        reason: reason || undefined,
        send_mail_new_account: sendEmail,
      });
      onOpenChange(false);
    } catch (error) {
      console.error('Error adding package:', error);
      toast.error('Failed to add package. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [
    selectedProduct,
    userId,
    validFrom,
    validTo,
    reason,
    sendEmail,
    onSave,
    onOpenChange,
  ]);

  const handleCancel = useCallback(() => {
    onOpenChange(false);
  }, [onOpenChange]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[620px]">
        <DialogHeader>
          <DialogTitle>
            Add Package for User &gt;{' '}
            {userId ? `LL${userId.toString().padStart(6, '0')}` : ''}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="package">Package</Label>
            <Select value={selectedProduct} onValueChange={setSelectedProduct}>
              <SelectTrigger>
                <SelectValue placeholder="Select..." />
              </SelectTrigger>
              <SelectContent>
                {productOptions.map((option) => (
                  <SelectItem key={option.value} value={String(option.value)}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {selectedProduct && (
            <>
              <div className="space-y-2">
                <Label htmlFor="validFrom">Valid From</Label>
                <DatePicker
                  value={validFrom}
                  onChange={(date) => setValidFrom(date)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="validTo">Valid To</Label>
                <DatePicker
                  value={validTo}
                  onChange={(date) => setValidTo(date)}
                />
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  checked={sendEmail}
                  id="sendEmail"
                  onCheckedChange={(checked) =>
                    setSendEmail(checked as boolean)
                  }
                />
                <Label className="cursor-pointer" htmlFor="sendEmail">
                  Send &lsquo;New Account&rsquo; email?
                </Label>
              </div>

              <div className="space-y-2">
                <Label htmlFor="reason">
                  Reason for creating package{' '}
                  <span className="text-red-500">*</span>
                </Label>
                <textarea
                  className={`w-full min-h-[100px] px-3 py-2 border rounded-md resize-none ${
                    reasonError ? 'border-red-500' : ''
                  }`}
                  id="reason"
                  placeholder="Enter reason..."
                  value={reason}
                  onChange={(e) => {
                    setReason(e.target.value);
                    if (reasonError) {
                      setReasonError('');
                    }
                  }}
                />
                {reasonError && (
                  <p className="text-sm text-red-500">{reasonError}</p>
                )}
              </div>

              <div className="flex gap-2 pt-4">
                <Button
                  className="bg-[#14CA74] hover:bg-[#14CA74]/90"
                  disabled={loading}
                  onClick={handleSave}
                >
                  SAVE
                </Button>
                <Button variant="destructive" onClick={handleCancel}>
                  CANCEL
                </Button>
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
